/**
 * Engagement tab — multi-layer sessions, DAU/WAU/MAU, feature usage.
 *
 * Layout:
 *   [ Multi-layer sessions stacked area (workouts/mobility/rest/external) ]
 *   [ DAU/WAU/MAU line + stickiness KPI row ]
 *   [ Feature usage event table ]
 */
import React from 'react';
import { Activity, Users } from 'lucide-react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  DauWauMauPayload,
  DepthWindow,
  EngagementDepthPayload,
  SessionsPayload,
} from '../types';
import { ChartCard } from '../components/ChartCard';
import { TrendChart } from '../components/TrendChart';
import { KpiTile } from '../components/KpiTile';
import { formatFloat, formatInt, formatPercent } from '../format';
import { formatAnalyticsDayShort, todayInAnalyticsTz } from '../timezone';

export interface EngagementTabProps {
  range: AnalyticsRange;
}

export const EngagementTab: React.FC<EngagementTabProps> = ({ range }) => {
  const sessions = useAnalyticsQuery<SessionsPayload>(
    ['sessions', range.queryString],
    `/api/admin/analytics/engagement/sessions?${range.queryString}`,
  );
  const dauWauMau = useAnalyticsQuery<DauWauMauPayload>(
    ['dau-wau-mau', range.queryString],
    `/api/admin/analytics/engagement/dau-wau-mau?${range.queryString}`,
  );
  const depth = useAnalyticsQuery<EngagementDepthPayload>(
    ['engagement-depth', range.queryString],
    `/api/admin/analytics/engagement/depth?${range.queryString}`,
  );

  const sessionPayload = sessions.data?.data;
  const dauPayload = dauWauMau.data?.data;
  const depthPayload = depth.data?.data;

  // The headline "Stickiness" KPI tile uses the latest *complete* day so the
  // value isn't a partial-day snapshot. Charts include today as a "live" point
  // (refreshed every 10 min by the analytics-rollups cron) — visibility wins
  // over a clean line, and today's bar/dot is annotated as in-progress.
  const todayIso = todayInAnalyticsTz();
  const allDauDays = dauPayload?.series ?? [];
  const completedDays = allDauDays.filter((d) => d.day !== todayIso);
  const latestDau = completedDays[completedDays.length - 1];
  const allSessionDays = sessionPayload?.series ?? [];

  return (
    <div className="space-y-6">
      {/* Top KPI row */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Sessions completed"
          value={formatInt(sessionPayload?.totals.cards_total)}
          icon={<Activity className="h-3 w-3" />}
          loading={sessions.isLoading}
        />
        <KpiTile
          label="Workouts (strict)"
          value={formatInt(sessionPayload?.totals.workouts_strict)}
          loading={sessions.isLoading}
        />
        <KpiTile
          label="Per active user / week"
          value={formatFloat(sessionPayload?.per_active_user_per_week, 2)}
          loading={sessions.isLoading}
        />
        <KpiTile
          label="Stickiness (DAU / MAU)"
          value={latestDau ? formatPercent(latestDau.stickiness_pct) : '—'}
          hint={
            latestDau
              ? `${formatInt(latestDau.dau)} DAU · ${formatInt(latestDau.mau)} MAU`
              : undefined
          }
          icon={<Users className="h-3 w-3" />}
          loading={dauWauMau.isLoading}
        />
      </div>

      {/* Engagement over Day / Week / Month — the quality of DAU (audit §10) */}
      <ChartCard
        title="Engagement over Day / Week / Month"
        subtitle="How many trials + subscribers were active (did something real — chat, workout, or a touchpoint), and how deep they went. Each window is computed over the whole window (not averaged from daily). 'Engaged' = the taps we capture (CTA / nudge / check-in / debrief)."
        loading={depth.isLoading}
        error={depth.error?.message ?? null}
        isEmpty={!depthPayload?.anchor_day}
        onRetry={() => depth.refetch()}
      >
        <DepthTriPanel payload={depthPayload} loading={depth.isLoading} />
      </ChartCard>

      {/* Multi-layer sessions — today is the rightmost point, refreshed every 10 min */}
      <ChartCard
        title="Sessions — multi-layer"
        subtitle="Workouts + mobility count toward 'Sessions completed'; rest days and external (device-only) activities are tracked but are NOT sessions. Today's bar refreshes every 10 minutes."
        loading={sessions.isLoading}
        error={sessions.error?.message ?? null}
        isEmpty={!allSessionDays.length}
        onRetry={() => sessions.refetch()}
      >
        <TrendChart
          data={allSessionDays}
          xKey="day"
          type="area"
          stacked
          height={300}
          series={[
            { key: 'workouts', label: 'Workouts' },
            { key: 'mobility', label: 'Mobility' },
            { key: 'rest', label: 'Rest days' },
            { key: 'external', label: 'External' },
          ]}
          xFormat={(iso) => formatDayShort(iso, todayIso)}
        />
      </ChartCard>

      {/* DAU/WAU/MAU — today included, refreshed every 10 min */}
      <ChartCard
        title="Active users (real activity)"
        subtitle="Daily / weekly / monthly trials + subscribers who did something real (chat, workout, or a touchpoint). True app-opens can't be captured on native, so this uses real activity. Today's point refreshes every 10 minutes."
        loading={dauWauMau.isLoading}
        error={dauWauMau.error?.message ?? null}
        isEmpty={!allDauDays.length}
        onRetry={() => dauWauMau.refetch()}
      >
        <TrendChart
          data={allDauDays}
          xKey="day"
          type="line"
          height={260}
          series={[
            { key: 'dau', label: 'DAU' },
            { key: 'wau', label: 'WAU' },
            { key: 'mau', label: 'MAU' },
          ]}
          xFormat={(iso) => formatDayShort(iso, todayIso)}
        />
      </ChartCard>

      {/* Engagement depth over time — are users sliding down the layers? */}
      <ChartCard
        title="Engagement depth over time"
        subtitle="Daily distinct users by depth: opened → conversed → trained. Opened flat while Trained slides = people show up but stop doing the work."
        loading={depth.isLoading}
        error={depth.error?.message ?? null}
        isEmpty={!depthPayload?.series?.length}
        onRetry={() => depth.refetch()}
      >
        <TrendChart
          data={depthPayload?.series ?? []}
          xKey="day"
          type="line"
          height={260}
          series={[
            { key: 'opened', label: 'Active' },
            { key: 'conversed', label: 'Conversed' },
            { key: 'trained', label: 'Trained' },
          ]}
          xFormat={(iso) => formatDayShort(iso, todayIso)}
        />
      </ChartCard>

      {/* "Event volume by type" table REMOVED (Batch 5): redundant now that the
          depth tri-panel + depth-over-time chart cover engagement, and it
          invited misreads (only events we emit appear — recals and memory
          recalls looked nonexistent). The backend telemetry endpoint remains
          for ad-hoc checks. */}
    </div>
  );
};

// Analytics-TZ-aware date formatter — see web/src/pages/admin/analytics/timezone.ts
const formatDayShort = formatAnalyticsDayShort;

// ── Engagement-depth tri-panel (audit §10) ─────────────────────────────────
// "Quality of DAU": of users who opened the app in a window, how many went
// deeper. Reach (% of openers reaching each layer) + Consistency (how often).

const DEPTH_LAYERS: Array<{ key: 'engaged' | 'conversed' | 'trained'; label: string }> = [
  { key: 'engaged', label: 'Engaged' },
  { key: 'conversed', label: 'Conversed' },
  { key: 'trained', label: 'Trained' },
];

const DepthCard: React.FC<{
  title: string;
  subtitle: string;
  win?: DepthWindow;
  showConsistency: boolean;
  loading: boolean;
}> = ({ title, subtitle, win, showConsistency, loading }) => {
  const reach = win?.reach;
  const cons = win?.consistency;
  const opened = reach?.opened ?? 0;
  const days = win?.window_days ?? 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <span className="text-[11px] text-gray-400">{subtitle}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tabular-nums" style={{ color: 'var(--cedar-ink)' }}>
          {loading ? '—' : formatInt(opened)}
        </span>
        <span className="text-xs text-gray-500">active</span>
      </div>

      <div className="mt-3 space-y-1.5">
        {DEPTH_LAYERS.map((l) => {
          const p = reach?.pct_of_opened?.[l.key] ?? null;
          const n = reach?.[l.key];
          return (
            <div key={l.key}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-600">{l.label}</span>
                <span className="tabular-nums text-gray-500">
                  {p == null ? '—' : `${formatInt(n)} · ${formatPercent(p)}`}
                </span>
              </div>
              <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(0, Math.min(100, p ?? 0))}%`,
                    backgroundColor: 'var(--coach-cedar, #1F6C4F)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {showConsistency && (
        <div className="mt-3 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
          {cons && cons.avg_open_days != null ? (
            <>
              opens{' '}
              <span className="font-semibold text-gray-700">{formatFloat(cons.avg_open_days, 1)}</span>/{days}d
              {' · '}
              trains{' '}
              <span className="font-semibold text-gray-700">{formatFloat(cons.avg_trained_days, 1)}</span>/{days}d
              {cons.stickiness_pct != null && <> {' · '}stickiness {formatPercent(cons.stickiness_pct)}</>}
            </>
          ) : (
            <span className="text-gray-400">warming up…</span>
          )}
        </div>
      )}
    </div>
  );
};

const DepthTriPanel: React.FC<{ payload?: EngagementDepthPayload; loading: boolean }> = ({
  payload,
  loading,
}) => {
  const w = payload?.windows;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <DepthCard title="Today" subtitle="latest complete day" win={w?.dau} showConsistency={false} loading={loading} />
      <DepthCard title="This Week" subtitle="trailing 7 days" win={w?.wau} showConsistency loading={loading} />
      <DepthCard title="This Month" subtitle="trailing 28 days" win={w?.mau} showConsistency loading={loading} />
    </div>
  );
};
